import {
    ExamApplicationDetails,
    ExamApplicationList,
    IExamDayApplications,
    IExamApplicationList,
} from '../../api/ApiClient';
import { createModel } from '@rematch/core';
import apiConnector from '../../api/ApiConnector';
import {
    GetListForDayData,
    IFetchExamAssignedApplicationsForDayProps,
    IFetchExamUnAssignedApplicationsProps,
} from 'components/models';
import { IExamApplicationsFilters } from 'components/reduxModels';

const initialState: ExamApplicationsRedux = {
    examApplicationUserAdmitted: false,
    examApplicationDetails: null,
    examApplications: null,
    finalizeApplicationsList: null,

    examUnAssignedApplicationsList: null,
    examApplicationsForDayList: null,
    unratedExamApplicationsList: null,
    examApplicationsListForAdmit: null,
};

export default createModel({
    state: initialState,
    reducers: {
        setExamUnAssignedApplicationsList(state, examUnAssignedApplicationsList: ExamApplicationList): ExamApplicationsRedux {
            return { ...(state || {}), examUnAssignedApplicationsList };
        },
        clearExamUnAssignedApplicationsList(state): ExamApplicationsRedux {
            return { ...(state || {}), examUnAssignedApplicationsList: null };
        },
        setExamApplicationsForDay(state: ExamApplicationsRedux, examApplicationsForDayList: IExamDayApplications): ExamApplicationsRedux {
            return { ...state, examApplicationsForDayList };
        },
        clearExamApplicationsForDay(state: ExamApplicationsRedux): ExamApplicationsRedux {
            return { ...state, examApplicationsForDayList: null };
        },
        setAdmitted(state, examApplicationUserAdmitted: boolean): ExamApplicationsRedux {
            return { ...(state || {}), examApplicationUserAdmitted };
        },
        setUnratedExamApplicationsList(state: ExamApplicationsRedux, unratedExamApplicationsList: IExamApplicationList): ExamApplicationsRedux {
            return { ...state, unratedExamApplicationsList };
        },
        clearUnratedExamApplicationsList(state: ExamApplicationsRedux): ExamApplicationsRedux {
            return { ...state, unratedExamApplicationsList: null };
        },
        setExamApplicationsListForAdmit(state: ExamApplicationsRedux, examApplicationsListForAdmit: IExamDayApplications): ExamApplicationsRedux {
            return { ...state, examApplicationsListForAdmit };
        },
        clearExamApplicationsListForAdmit(state: ExamApplicationsRedux): ExamApplicationsRedux {
            return { ...state, examApplicationsListForAdmit: null };
        },
        setDetails(state, examApplicationDetails: ExamApplicationsRedux['examApplicationDetails']): ExamApplicationsRedux {
            return { ...(state || {}), examApplicationDetails };
        },
        clearDetails(state): ExamApplicationsRedux {
            return { ...(state || {}), examApplicationDetails: null };
        },
        setList(state, examApplications: ExamApplicationsRedux['examApplications']): ExamApplicationsRedux {
            return { ...state, examApplications };
        },
        setFinalizeApplicationList(state, finalizeApplicationsList: ExamApplicationsRedux['finalizeApplicationsList']): ExamApplicationsRedux {
            return { ...(state || {}), finalizeApplicationsList };
        },
        clearExamApplications(state) {
            return { ...state, ...initialState };
        },
    },
    effects: (dispatch: any) => ({
        async getExamApplications(payload: IExamApplicationsFilters) {
            const examApplications = await apiConnector.execApi(dispatch, api => api.getExamApplications(
                payload.examId,
                payload.searchField,
                payload.searchTerm,
                payload.page,
                payload.pageSize || 100,
                payload.sortOrder,
                payload.sortField,
            ));

            this.setList(examApplications);
        },
        async fetchExamApplicationDetails(examApplicationId: string) {
            const examApplicationDetails = await apiConnector.execApi(dispatch, api => api.getExamApplication(examApplicationId));

            this.setDetails(examApplicationDetails);

            return examApplicationDetails;
        },
        async getExamApplicationsForFinalizeAsync(data: IGetExamApplicationsForFinalize) {
            const finalizeApplicationsList = await apiConnector.execApi(dispatch, api => api.getExamApplicationsForFinalize(
                data.examId,
                data.searchField,
                data.searchTerm,
                data.page,
                data.pageSize,
                data.sortOrder,
                data.sortField,
            ));

            this.setFinalizeApplicationList(finalizeApplicationsList);
        },
        async fetchExamUnAssignedApplicationsList(payload: IFetchExamUnAssignedApplicationsProps) {
            const list = await apiConnector.execApi(dispatch, api => api.getUnassignedExamApplicationsForStage(
              payload.examId,
              payload.stage,
              payload.page,
              payload.pageSize,
              payload.sortOrder,
              payload.sortField,
            ));
            this.setExamUnAssignedApplicationsList(list);
        },
        async fetchExamApplicationsForDayAsync(data: IFetchExamAssignedApplicationsForDayProps) {
            const examApplicationsListForDay = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamDayApplications(
              data.examId,
              data.stage,
              data.examDayId,
              data.page,
              data.pageSize,
              data.sortOrder,
              data.sortField,
            ));

            this.setExamApplicationsForDay(examApplicationsListForDay);
        },
        async fetchUnratedExamApplications(data: GetListForDayData) {
            const unratedExamApplicationsList = await apiConnector.execApi(dispatch, apiClient => apiClient.getUnratedExamApplications(
              data.examDayId,
              data.page,
              data.pageSize,
              data.sortOrder,
              data.sortField,
            ));

            this.setUnratedExamApplicationsList(unratedExamApplicationsList);
        },
        async fetchExamApplicationsForDayAdmitAsync(data: IFetchExamAssignedApplicationsForDayProps) {
            const examApplicationsListForDayAdmit = await apiConnector.execApi(dispatch, apiClient => apiClient.getExamDayApplications(
              data.examId,
              data.stage,
              data.examDayId,
              data.page,
              data.pageSize,
              data.sortOrder,
              data.sortField,
            ));

            this.setExamApplicationsListForAdmit(examApplicationsListForDayAdmit);
        },
    }),
});

interface IGetExamApplicationsForFinalize {
    examId?: string | undefined;
    searchField?: string;
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean | undefined;
    sortField?: string;
}

interface ExamApplicationsRedux {
    examApplicationUserAdmitted: boolean;
    examApplicationDetails: ExamApplicationDetails | null;
    examApplications: ExamApplicationList | null;
    finalizeApplicationsList: ExamApplicationList | null;

    examUnAssignedApplicationsList: ExamApplicationList | null;
    examApplicationsForDayList: IExamDayApplications | null;
    unratedExamApplicationsList: IExamApplicationList | null;
    examApplicationsListForAdmit: IExamDayApplications | null;
}
